import { ProgressionState } from "./GameState";
import { Machine } from "./Machine";
import { playerAttendsMachine } from "./machine-helpers";
import { getOperationPhases } from "./skill-helpers";
import { Vector } from "./Vectors";

/**
 * The audible state of a machine, derived fresh from game state every frame
 * (see `MachineSoundLayer`). Deriving rather than listening for start/stop
 * events means the sound is always consistent with the simulation — an
 * operation that pauses when the player walks off, resumes, or comes back
 * from a save reload just sounds right, with no events to miss.
 *
 *  - "off":     no power — nothing in progress (or a hands-free phase like
 *               glue curing), or the machine's power switch is off
 *  - "running": the motor idles — a switched machine is on but not biting
 *               wood, the player stepped away or let go of the operate key
 *               mid-operation, or the machine is waiting at a phase boundary
 *  - "cutting": an attended phase is actively ticking
 */
export type MachineSoundPhase = "off" | "running" | "cutting";

export function deriveMachineSoundPhase(
  machine: Machine,
  playerPosition: Vector,
  playerIsAway: boolean,
  playerIsOperating: boolean,
  progression: ProgressionState,
): MachineSoundPhase {
  // A machine with a power switch is audible from the switch, not the work:
  // off is silent even mid-operation (the cut is paused), and on means the
  // motor idles between cuts — the running sound is what reminds the player
  // they left the planer on.
  const switched = machine.type.powerSwitch === true;
  if (switched && !machine.isPowered) {
    return "off";
  }
  const idle: MachineSoundPhase = switched ? "running" : "off";

  const progress = machine.operationProgress;
  if (progress.status !== "inProgress") {
    return idle;
  }
  const operation = machine.selectedOperationOrNull;
  if (!operation) {
    return idle;
  }

  const phases = getOperationPhases(operation, progression);
  const phase = phases[Math.min(progress.phaseIndex, phases.length - 1)];

  // Hands-free phases (glue curing) make no machine noise.
  if (!phase.attended) {
    return idle;
  }

  // Same rule the tick uses (see MachineSystem): an attended phase only bites
  // wood while the player stands there *holding the operate key*. Let go of
  // Space at the jointer and the blade is still spinning, but nothing is
  // being fed through it — so the cut sound (and the sawdust the sprites
  // spray from it) stops with the feed.
  //
  // Power feed keeps cutting (and screaming) with nobody standing there.
  return operation.powerFeed ||
    (playerAttendsMachine(machine, playerPosition, playerIsAway) &&
      playerIsOperating)
    ? "cutting"
    : "running";
}

/** What a machine's art shows: blade motion, and chips coming off the cut. */
export interface MachineVisuals {
  /** The machine is biting wood — drive cut particles from this. */
  readonly working: boolean;
  /** The motor is turning — drive animation/shake from this. */
  readonly powered: boolean;
}

/**
 * What the eye shows, given what the ear hears.
 *
 * A machine with a continuous voice takes both from its audible phase, so
 * the blade is already spinning through the spin-up and keeps spinning
 * through the coast-down — neither of which the shop's own state knows
 * about, since the lead-in/lead-out sequencing lives in wall-clock time
 * inside the voice. Pass `null` for a machine with no voice and both
 * follow the operation itself.
 */
export function deriveMachineVisuals(
  audiblePhase: MachineSoundPhase | null,
  operationWorking: boolean,
): MachineVisuals {
  if (audiblePhase === null) {
    return { working: operationWorking, powered: operationWorking };
  }
  return {
    working: audiblePhase === "cutting",
    powered: audiblePhase !== "off",
  };
}
